"use client";
import { EnrichedProgram, ProcessedCourse } from "@/common/course/types";
import ExpandableRelatedPrograms from "@/components/CourseContent/ExpandableRelatedPrograms";
import FullProgramCTA from "@/components/CourseContent/FullProgramCTA";
import ProgramCarousel, {
  CardType,
} from "@/components/CourseContent/ProgramCarousel";
import {
  CourseCarouselData,
  CourseProgramExpandableSection,
} from "@/monarch/rules/singleCourseTraffic/types";
import { InstantSearch, Configure } from "react-instantsearch";
import getPublicClient from "@/lib/algolia/getPublicClient";
import useEnrichedSearchHits from "@/hooks/useEnrichedSearchHits";
import getFilters from "@/components/Search/utils/getFilters";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";

type Props = {
  designTobeSelected?: string;
  enrichedPrograms?: EnrichedProgram[];
  course: ProcessedCourse;
  carouselData?: CourseCarouselData;
  cardType?: CardType;
  expandableSection?: CourseProgramExpandableSection;
  carouselDataDesign3?:
    | {
        title?: string;
        description?: string;
        highlightText?: string;
      }
    | undefined;
  ignorePricingData?: boolean;
  ignoreSubtitle?: boolean;
};

export default function RelatedProgram(props: Props) {
  const {
    designTobeSelected,
    enrichedPrograms,
    course,
    carouselData,
    cardType,
    expandableSection,
    carouselDataDesign3,
    ignorePricingData,
    ignoreSubtitle,
  } = props;

  const searchClient = getPublicClient();
  const partnersForProgramSearch = course.partners.map(
    (partner) => partner.name,
  );

  const configForSearch = {
    filters: getFilters({ productType: "program" }),
    disjunctiveFacets: ["partner"],
    disjunctiveFacetsRefinements: { partner: partnersForProgramSearch || [] },
    facets: ["product"],
    clickAnalytics: true,
    facetsRefinements: { product: ["program"] },
    hitsPerPage: 4,
  };

  return (
    <InstantSearch searchClient={searchClient} indexName={PRODUCT_INDEX_EN}>
      <Configure {...configForSearch} />
      <RelatedProgramContent
        designTobeSelected={designTobeSelected}
        enrichedPrograms={enrichedPrograms}
        course={course}
        carouselData={carouselData}
        cardType={cardType}
        expandableSection={expandableSection}
        carouselDataDesign3={carouselDataDesign3}
        ignorePricingData={ignorePricingData}
        ignoreSubtitle={ignoreSubtitle}
      />
    </InstantSearch>
  );
}

function RelatedProgramContent({
  designTobeSelected,
  enrichedPrograms,
  course,
  carouselData,
  cardType,
  expandableSection,
  carouselDataDesign3,
  ignorePricingData = false,
  ignoreSubtitle = false,
}: Props) {
  const { enrichedHits, isLoading: isEnrichingHits } = useEnrichedSearchHits({
    ignorePricingData: ignorePricingData,
    ignoreSubtitle: ignoreSubtitle,
  });
  switch (designTobeSelected) {
    case "1":
      return (
        <ProgramCarousel
          key="programCarousel"
          enrichedItems={
            enrichedPrograms && enrichedPrograms?.length > 0
              ? enrichedPrograms
              : enrichedHits
          }
          courseName={course.productName}
          carouselData={carouselData}
          cardType={cardType ?? "new-product-card"}
          isLoading={isEnrichingHits}
          navigationType="individual-buttons"
        />
      );

    case "2":
      return (
        <ExpandableRelatedPrograms
          title={expandableSection?.title || ""}
          subtitle={expandableSection?.subtitle}
          reasons={expandableSection?.reasons}
          programs={
            enrichedPrograms && enrichedPrograms?.length > 0
              ? enrichedPrograms
              : enrichedHits
          }
          backgroundColor={expandableSection?.backgroundColor}
          showMoreLabel={expandableSection?.showMoreLabel}
          showLessLabel={expandableSection?.showLessLabel}
          showNumberOfPrograms={
            expandableSection?.descriptionFeaturedProgramsAppend
          }
          cardType={cardType ?? "program-card"}
        />
      );

    case "3":
      return (
        <ProgramCarousel
          key="programCarousel"
          enrichedItems={
            enrichedPrograms && enrichedPrograms?.length > 0
              ? enrichedPrograms
              : enrichedHits
          }
          courseName={course.productName}
          isButtonsCentered={true}
          carouselData={carouselData}
          cardType={cardType ?? "new-product-card"}
          isLoading={isEnrichingHits}
          navigationType="individual-buttons"
          hasShadow={false}
          carouselDataDesign3={carouselDataDesign3}
        />
      );

    default:
      return enrichedPrograms && enrichedPrograms.length > 0 ? (
        <FullProgramCTA key="fullProgramCta" items={course?.programs ?? []} />
      ) : null;
  }
}
